'use client';

import AppImage from '../../ui/AppImage';
import Icon from '../../ui/AppIcon';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import type {
  ContentArticlePublic,
  ContentTestimonialPublic,
  PaginatedContentArticles,
} from '@/lib/types/content.types';

interface ArticleCard {
  id: string;
  slug?: string;
  title: string;
  excerpt: string;
  image: string;
  alt: string;
  date: string;
  category: string;
}

interface TestimonialCard {
  id: string;
  name: string;
  role: string;
  image: string;
  alt: string;
  content: string;
  rating: number;
}

function mapArticle(item: ContentArticlePublic): ArticleCard {
  return {
    id: String(item.id),
    slug: item.slug ?? undefined,
    title: item.title ?? 'Untitled',
    excerpt: item.excerpt ?? '',
    image: item.image || '/resource/placeholder.png',
    alt: item.alt ?? item.title ?? 'Article',
    date: item.date ?? '',
    category: item.category ?? 'Hair Treatment',
  };
}

function mapTestimonial(item: ContentTestimonialPublic): TestimonialCard {
  return {
    id: String(item.id),
    name: item.name ?? 'Client',
    role: item.role ?? '',
    image: item.image || '/resource/placeholder.png',
    alt: item.alt ?? item.name ?? 'Client',
    content: item.content ?? '',
    rating: item.rating ?? 5,
  };
}

export default function NewsTestimonialsSection() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [articles, setArticles] = useState<ArticleCard[]>([]);
  const [testimonials, setTestimonials] = useState<TestimonialCard[]>([]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [articlesRes, testimonialsRes] = await Promise.all([
          fetch('/api/content/articles?limit=3&page=1'),
          fetch('/api/content/testimonials'),
        ]);
        if (!mounted) return;
        if (articlesRes.ok) {
          const payload = (await articlesRes.json()) as PaginatedContentArticles;
          setArticles((payload.data ?? []).map(mapArticle));
        }
        if (testimonialsRes.ok) {
          const payload =
            (await testimonialsRes.json()) as ContentTestimonialPublic[];
          const mapped = (Array.isArray(payload) ? payload : []).map(
            mapTestimonial,
          );
          setTestimonials(mapped);
          setActiveTestimonial(0);
        }
      } catch {
        // Leave lists empty when the API is unavailable.
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, []);

  const nextTestimonial = () => {
    if (testimonials.length === 0) return;
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    if (testimonials.length === 0) return;
    setActiveTestimonial(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length,
    );
  };

  const active = testimonials[activeTestimonial] ?? testimonials[0];

  return (
    <section id="news" className="py-20 md:py-32 bg-secondary reveal">
      <div className="container mx-auto px-6">
        <div className="mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              HAIR CARE TIPS & INSIGHTS
            </h2>
            <p className="text-lg text-muted-foreground">
              Stay informed with the latest hair care trends and styling tips.
            </p>
          </motion.div>

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {articles.map((article, index) => {
                const href = article.slug
                  ? `/insights/${article.slug}`
                  : '/insights';
                return (
                  <motion.article
                    key={article.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    whileHover={{ y: -8 }}
                    className="bg-card rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group">
                    <Link href={href} className="block">
                      <div className="relative h-64 overflow-hidden">
                        <AppImage
                          src={article.image}
                          alt={article.alt}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-semibold">
                            {article.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        {article.date ? (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                            <Icon name="CalendarIcon" size={16} />
                            <span>{article.date}</span>
                          </div>
                        ) : null}
                        <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-muted-foreground mb-4 leading-relaxed">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center gap-2 text-primary font-medium group-hover:gap-3 transition-all">
                          <span>Read More</span>
                          <Icon
                            name="ArrowRightIcon"
                            size={18}
                            className="group-hover:translate-x-1 transition-transform"
                          />
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                );
              })}
            </div>
          ) : null}

          <div className="mt-10 text-center">
            <Link
              href="/insights"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90">
              View all tips
              <Icon name="ArrowRightIcon" size={18} />
            </Link>
          </div>
        </div>

        <div id="testimonials">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              WHAT OUR
              <br />
              CLIENTS SAY
            </h2>
            <p className="text-lg text-muted-foreground">
              Hear from our satisfied clients.
            </p>
          </motion.div>

          {active ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto">
              <div className="bg-card rounded-3xl shadow-2xl p-8 md:p-12 relative">
                <div className="absolute top-8 left-8 text-primary/20">
                  <Icon name="ChatBubbleLeftIcon" size={48} />
                </div>
                <div className="relative z-10">
                  <div className="flex flex-col md:flex-row items-center gap-8 mb-8">
                    <div className="flex-shrink-0">
                      <div className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-primary/20">
                        <AppImage
                          src={active.image}
                          alt={active.alt}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    <div className="text-center md:text-left">
                      <h3 className="text-2xl font-bold text-foreground mb-1">
                        {active.name}
                      </h3>
                      <p className="text-muted-foreground mb-3">{active.role}</p>
                      <div className="flex gap-1 justify-center md:justify-start">
                        {[...Array(active.rating)].map((_, i) => (
                          <Icon
                            key={`star_${i}`}
                            name="StarIcon"
                            size={20}
                            variant="solid"
                            className="text-yellow-400"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-lg text-foreground leading-relaxed mb-8 italic">
                    &quot;{active.content}&quot;
                  </p>
                  {testimonials.length > 1 ? (
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        {testimonials.map((_, index) => (
                          <button
                            key={`dot_${index}`}
                            onClick={() => setActiveTestimonial(index)}
                            className={`w-2 h-2 rounded-full transition-all ${
                              index === activeTestimonial
                                ? 'bg-primary w-8'
                                : 'bg-muted'
                            }`}
                            aria-label={`Go to testimonial ${index + 1}`}
                          />
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={prevTestimonial}
                          className="w-10 h-10 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center"
                          aria-label="Previous testimonial">
                          <Icon name="ChevronLeftIcon" size={20} />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={nextTestimonial}
                          className="w-10 h-10 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center"
                          aria-label="Next testimonial">
                          <Icon name="ChevronRightIcon" size={20} />
                        </motion.button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </motion.div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
